<template>
  <div class="usage-card">
    <div v-if="formattedUsageItems.length > 0" class="usage-items-container">
      <div
          v-for="item in formattedUsageItems"
          :key="item.productKey"
          class="usage-item"
      >
        <div class="usage-header">
          <span class="usage-label">{{ item.displayName }}</span>
          <span v-if="item.isExceeded" class="exceeded-badge">
            {{ $t('limit_exceeded') }}
          </span>
        </div>
        <div class="progress-bar">
          <div
              class="progress-fill"
              :style="{ width: Math.min(item.usagePercentage, 100) + '%' }"
              :class="{
                'progress-exceeded': item.isExceeded,
                'progress-danger': !item.isExceeded && item.usagePercentage > 90,
                'progress-warning': !item.isExceeded && item.usagePercentage > 70 && item.usagePercentage <= 90,
                'progress-success': !item.isExceeded && item.usagePercentage <= 70
              }"
          ></div>
        </div>
        <div class="usage-info">
          <span class="usage-count" :class="{ 'usage-danger': item.isExceeded }">
            {{ $t('used_format', { used: item.usedLimit, total: item.assignedLimit, unit: $t('unit_' + item.unitKey) }) }}
          </span>
          <span class="usage-remaining" :class="{ 'usage-danger': item.isExceeded }">
            {{ $t('remaining_format', { remaining: item.remainingLimit, unit: $t('unit_' + item.unitKey) }) }}
          </span>
          <span v-if="item.isExceeded && item.overLimit > 0" class="usage-over">
            {{ $t('over_limit_format', { over: item.overLimit, unit: $t('unit_' + item.unitKey) }) }}
          </span>
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="usage-items-container">
      <div v-for="i in skeletonCount" :key="i" class="usage-item">
        <div class="usage-header">
          <span class="usage-label skeleton-text">{{ $t('loading') }}...</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill skeleton-loading"></div>
        </div>
        <span class="usage-count skeleton-text">--/-- --</span>
      </div>
    </div>

    <div v-else-if="error" class="usage-error">
      <span class="no-data-found">{{ $t('no_data_found') }}</span>
    </div>

    <div v-else class="usage-empty">
      <div class="empty-icon">
        <span class="no-data-found">{{ $t('no_data_found') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UsageCard',

  props: {
    usageItems: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    skeletonCount: {
      type: Number,
      default: 6
    }
  },

  methods: {
    extractUnit(limitType) {
      if (!limitType) return 'count';
      const match = limitType.match(/[a-zA-Z]+$/);
      return match ? match[0].toLowerCase() : 'count';
    }
  },

  computed: {
    formattedUsageItems() {
      if (!this.usageItems || !Array.isArray(this.usageItems)) return [];

      return this.usageItems.map(item => {
        const assignedLimit = item.assignedLimit ?? 0;
        const usedLimit = item.usedLimit ?? 0;
        const remainingLimit = item.remainingLimit ?? 0;
        const overLimit = item.overLimit ?? 0;
        const isExceeded = item.isExceeded ?? false;

        let usagePercentage = 0;
        if (assignedLimit > 0) {
          usagePercentage = Math.round((usedLimit / assignedLimit) * 100);
        }

        return {
          productKey: item.productKey,
          name: item.name,
          displayName: item.displayName || item.name,
          limitType: item.limitType,
          unitKey: this.extractUnit(item.limitType),
          displayText: item.displayText,
          assignedLimit,
          usedLimit,
          remainingLimit,
          overLimit,
          isExceeded,
          usagePercentage
        };
      });
    }
  }
}
</script>

<style scoped>
.usage-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  width: 100%;
}

.usage-items-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.usage-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.usage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.usage-label {
  font-weight: 500;
  color: #1a1a1a;
  font-size: 14px;
  font-family: 'euclid_medium', sans-serif;
}

.exceeded-badge {
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: #ef4444;
  padding: 2px 8px;
  border-radius: 12px;
  font-family: 'euclid_medium', sans-serif;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #f2f4f7;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.progress-success {
  background: #10b981;
}

.progress-warning {
  background: #f59e0b;
}

.progress-danger {
  background: #ef4444;
}

.progress-exceeded {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
  animation: pulse-danger 1.5s ease-in-out infinite;
}

@keyframes pulse-danger {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.usage-info {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
  font-family: 'euclid_regular', sans-serif;
}

.usage-count,
.usage-remaining {
  font-size: 12px;
}

.usage-over {
  color: #ef4444;
  font-weight: 500;
}

.usage-danger {
  color: #ef4444 !important;
  font-weight: 500;
}

.usage-error,
.usage-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.skeleton-loading {
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.skeleton-text {
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  color: transparent;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 768px) {
  .usage-card {
    padding: 16px;
  }
  .usage-info {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
